import { useRef, useState } from "react";
import { IMAGES } from "../lib/site";
import { FadeUp, MaskedLine } from "./Reveal";
import { MoveHorizontal } from "lucide-react";

const PAIRS = [
    {
        id: "emerald-suite",
        label: "The Emerald Suite",
        before: IMAGES.slider_before_1,
        after: IMAGES.slider_after_1,
    },
    {
        id: "mineral-bath",
        label: "The Mineral Bath",
        before: IMAGES.slider_before_2,
        after: IMAGES.slider_after_2,
    },
];

const Compare = ({ pair }) => {
    const ref = useRef(null);
    const [pos, setPos] = useState(50);
    const [dragging, setDragging] = useState(false);

    const update = (clientX) => {
        const rect = ref.current.getBoundingClientRect();
        setPos(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
    };

    return (
        <div
            ref={ref}
            data-testid={`ba-slider-${pair.id}`}
            className="relative h-[54vh] sm:h-[58vh] max-h-[540px] mx-auto aspect-[2/3] max-w-full overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] select-none touch-none cursor-ew-resize shadow-[0_30px_80px_rgba(0,0,0,0.4)]"
            onPointerDown={(e) => {
                e.currentTarget.setPointerCapture?.(e.pointerId);
                setDragging(true);
                update(e.clientX);
            }}
            onPointerMove={(e) => dragging && update(e.clientX)}
            onPointerUp={() => setDragging(false)}
            onPointerCancel={() => setDragging(false)}
        >
            <img
                src={pair.after}
                alt={`${pair.label} after renovation`}
                className="absolute inset-0 h-full w-full object-cover img-treated"
                draggable={false}
            />
            <img
                src={pair.before}
                alt={`${pair.label} before renovation`}
                draggable={false}
                className="absolute inset-0 h-full w-full object-cover"
                style={{
                    clipPath: `inset(0 ${100 - pos}% 0 0)`,
                    transition: dragging ? "none" : "clip-path 0.5s cubic-bezier(0.22,1,0.36,1)",
                }}
            />

            <span className="absolute top-5 left-5 z-10 rounded-full bg-noir/70 px-4 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-white/90 backdrop-blur">
                Before
            </span>
            <span className="absolute top-5 right-5 z-10 rounded-full bg-brass px-4 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-noir">
                After
            </span>

            <div
                className="absolute inset-y-0 z-10 w-px bg-white/90"
                style={{
                    left: `${pos}%`,
                    transition: dragging ? "none" : "left 0.5s cubic-bezier(0.22,1,0.36,1)",
                }}
            >
                <span className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full border border-white/70 bg-noir/60 text-white backdrop-blur">
                    <MoveHorizontal className="h-5 w-5" />
                </span>
            </div>
        </div>
    );
};

const BeforeAfter = () => {
    const [active, setActive] = useState(0);

    return (
        <section id="transformations" className="bg-mineral text-ink" data-testid="before-after-section">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-10 py-16 sm:py-20">
                <div className="flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between mb-8 sm:mb-12">
                    <div>
                        <FadeUp>
                            <p className="font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase text-sand mb-6">
                                Before / After
                            </p>
                        </FadeUp>
                        <h2 className="font-serif font-light leading-[1.05] tracking-tight text-[clamp(2.2rem,4.5vw,4rem)]">
                            <MaskedLine>Drag to see</MaskedLine>
                            <MaskedLine delay={0.1}>
                                what <em className="italic">one</em> renovation changes.
                            </MaskedLine>
                        </h2>
                    </div>
                    <FadeUp delay={0.2}>
                        <div className="flex gap-2" data-testid="ba-tabs">
                            {PAIRS.map((p, i) => (
                                <button
                                    key={p.id}
                                    data-testid={`ba-tab-${p.id}`}
                                    onClick={() => setActive(i)}
                                    className={`rounded-full border px-5 py-2.5 text-xs sm:text-sm font-medium transition-all duration-300 ${
                                        active === i
                                            ? "border-ink bg-ink text-white"
                                            : "border-ink/20 text-ink/60 hover:border-ink/50"
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </FadeUp>
                </div>

                <FadeUp>
                    <Compare pair={PAIRS[active]} />
                </FadeUp>

                <FadeUp delay={0.15}>
                    <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/40">
                            Illustrative transformations &mdash; drag the handle
                        </p>
                        <p className="text-xs sm:text-sm font-light text-ink/50">
                            Your bathroom could be next. A Spaces by Samita venture.
                        </p>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
};

export default BeforeAfter;
